<template>
  <div
    :data-widget-title="content.optionalTitle?.text"
    :class="content.layout === 'free_fluid' ? 'mask-wave-t-b bg-white' : 'last:mb-0'"
  >
    <SliceTitle
      class="container text-center pt-16"
      :class="content.layout === 'boxed_33' ? 'subtitle1' : 'title lg:text-center'"
      :value="content.optionalTitle"
    />
    <div
      class="mt-6"
      :class="{
        'lg:flex': content.layout === 'boxed_100' || content.layout === 'free_fluid',
        'container pb-16': content.layout === 'free_fluid',
      }"
    >
      <div
        class="border-b border-gray-200"
        :class="{
          'p-6': content.layout === 'boxed_33',
          'p-box-2 lg:border-r': content.layout === 'boxed_66',
          'px-box-2': content.layout === 'boxed_100',
          'py-box-2 lg:pr-box-2 lg:w-3/5 lg:border-b-0 lg:border-r':
            content.layout === 'boxed_100' || content.layout === 'free_fluid',
        }"
      >
        <div class="text-center" :class="{ invisible: !formattedRate }">
          <a class="block subtitle1 hover:underline" :href="currencyPageUrl">
            1 {{ content.corridor.currencyFrom.iso }} = {{ formattedRate }}
            {{ content.corridor.currencyTo.iso }}
          </a>
          <span class="text-16 font-semibold mt-1.5">{{ $t('currency_chart.description') }}</span>
        </div>

        <CurrencyChart
          class="w-full mt-6"
          :has-dropdown="content.layout === 'boxed_33'"
          :currency-from="content.corridor.currencyFrom.iso"
          :currency-to="content.corridor.currencyTo.iso"
          :mid-market-rate="liveRate"
          :is-hidden="showInput && content.layout === 'boxed_33'"
          @toggle-chart="toggleInput"
        />
      </div>
      <div
        class="flex items-center justify-center"
        :class="{
          'p-6': content.layout === 'boxed_33',
          'p-box-4 lg:pt-box-2': content.layout === 'boxed_66',
          'px-box-4': content.layout === 'boxed_100',
          'pt-box-4 pb-box-2 lg:py-box-2 lg:pl-box-2 lg:w-2/5':
            content.layout === 'boxed_100' || content.layout === 'free_fluid',
        }"
      >
        <EmailAlert
          class="self-center w-full md:max-w-lg block"
          :title="{ style: 'h3', text: $t('email_alert.alert_title') }"
          :description="$t('email_alert.alert_description')"
          :cta-label="$t('email_alert.create_alert')"
          :options="options"
          :alert="{ requestBody, requestPath: '/subscriptions' }"
          :is-input-visible="showInput || content.layout !== 'boxed_33'"
          @toggle-input-visibility="toggleInput"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { GQLCorridorEmailAlertSlice } from './generated/corridor-email-alert'
import type { ExchangeRateEmailAlertRequestBody } from '@core/components/email-alert/type'

const props = defineProps<{ content: GQLCorridorEmailAlertSlice }>()

const { $t, $lang, $cookie } = useNuxtApp()

const options = computed(() => {
  return [
    {
      value: 'EXCHANGE_RATE_DAILY',
      label: $t('email_alert.every_day'),
      description: $t('email_alert.monday_to_friday'),
    },
    {
      value: 'EXCHANGE_RATE_BI_WEEKLY',
      label: $t('email_alert.every_two_weeks'),
    },
  ]
})
const route = useRoute()

const amount = computed(() => {
  const amount = Number(route.params.amount)
  return amount > 0 ? amount : (props.content.corridor.defaultAmount ?? 1000)
})

const requestBody = computed<ExchangeRateEmailAlertRequestBody>(() => {
  return {
    userId: $cookie.value.getId(),
    language: $lang,
    comparisonId: null,
    countryFrom: props.content.corridor.countryFrom.iso,
    countryTo: props.content.corridor.countryTo.iso,
    currencyFrom: props.content.corridor.currencyFrom.iso,
    currencyTo: props.content.corridor.currencyTo.iso,
    amount: amount.value,
    params: { tabType: '' },
    creationLocation: 'corridor_email_alert_slice',
  }
})

const liveRate = useLiveExchangeRate(
  props.content.corridor.currencyFrom.iso,
  props.content.corridor.currencyTo.iso,
)

const formattedRate = computed(() => {
  return liveRate.value ? formatRate(liveRate.value, $lang) : undefined
})

const currencyPageUrl = computed(() => getCurrencyUrl(props.content.corridor))

const showInput = ref(false)
function toggleInput() {
  showInput.value = !showInput.value
}
</script>
